import { Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { AdminShell } from "@/components/admin/admin-shell";
import { LegacyAdminShell } from "@/components/admin/legacy/legacy-admin-shell";
import { CustomerVoiceRealtimeListener } from "@/components/admin/customer-voice-realtime-listener";
import { adminPageRoutes } from "@/routes/generated-pages";
import MaintenancePage from "@/pages/maintenance/page";
import ForbiddenPage from "@/pages/forbidden/page";
import { usePathname } from "@/compat/navigation";
import { requiredRightForPath } from "@/lib/admin-route-access";
import { useAdminAccessStore } from "@/stores/admin-access-store";
import { useAuthStore } from "@/stores/auth-store";

const useLegacyUi =
  import.meta.env.VITE_USE_LEGACY_ADMIN_SHELL === "1" ||
  import.meta.env.VITE_USE_LEGACY_ADMIN_SHELL === "true";

const maintenanceMode = ["1", "true", "yes", "on"].includes(
  (import.meta.env.VITE_MAINTENANCE_MODE ?? "").trim().toLowerCase(),
);

function AccessBoundOutlet() {
  const pathname = usePathname() ?? "";
  const token = useAuthStore((s) => s.token);
  const loaded = useAdminAccessStore((s) => s.loaded);
  const rights = useAdminAccessStore((s) => s.rights);
  const load = useAdminAccessStore((s) => s.load);
  const clear = useAdminAccessStore((s) => s.clear);

  useEffect(() => {
    if (token) void load();
    else clear();
  }, [token, load, clear]);

  if (token && !loaded) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted">
        Loading…
      </div>
    );
  }

  const need = requiredRightForPath(pathname);
  if (
    need &&
    !rights.some((r) => r.toLowerCase() === need.toLowerCase())
  ) {
    return <ForbiddenPage />;
  }

  return <Outlet />;
}

function AuthenticatedLayout() {
  // One SignalR + toast/sound listener for the whole logged-in admin session
  // (any page), not only Customer Voice. Requires CustomerVoiceIndex right.
  const shell = useLegacyUi ? (
    <LegacyAdminShell>
      <AccessBoundOutlet />
    </LegacyAdminShell>
  ) : (
    <AdminShell>
      <AccessBoundOutlet />
    </AdminShell>
  );

  return (
    <>
      <CustomerVoiceRealtimeListener />
      {shell}
    </>
  );
}

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted">
      Loading…
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/settings/ai-knowledge" element={<Navigate to="/ai/knowledge" replace />} />
      <Route path="/settings/birthday-gift" element={<Navigate to="/campaigns/birthday" replace />} />

      {adminPageRoutes
        .filter((r) => r.path === "/login")
        .map(({ path, Component }) => (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={<RouteFallback />}>
                <Component />
              </Suspense>
            }
          />
        ))}

      <Route element={<AuthenticatedLayout />}>
        {adminPageRoutes
          .filter((r) => r.path !== "/login")
          .map(({ path, Component }) => (
            <Route
              key={path}
              path={path}
              element={
                <Suspense fallback={<RouteFallback />}>
                  <Component />
                </Suspense>
              }
            />
          ))}
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  if (maintenanceMode) {
    return (
      <ThemeProvider>
        <MaintenancePage />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ThemeProvider>
  );
}
